import { useState, useEffect } from 'react';
import { Activity, Server, Database, Cpu, HardDrive, AlertTriangle, CheckCircle, Clock, RefreshCw, Trash2, Zap, Shield } from 'lucide-react';

interface HealthCheckResult {
  status: 'healthy' | 'warning' | 'critical';
  timestamp: string;
  checks: {
    memory: {
      status: 'healthy' | 'warning' | 'critical';
      heapUsedMB: number;
      heapTotalMB: number;
      heapLimitMB: number;
      rssMB: number;
      percentUsed: number;
      message: string;
    };
    cpu: {
      status: 'healthy' | 'warning' | 'critical';
      usagePercent: number;
      loadAverage: number[];
      message: string;
    };
    processes: {
      status: 'healthy' | 'warning' | 'critical';
      zombieCount: number;
      totalProcesses: number;
      message: string;
    };
    database: {
      status: 'healthy' | 'warning' | 'critical';
      connected: boolean;
      responseTime: number;
      message: string;
    };
    intervals: {
      status: 'healthy' | 'warning' | 'critical';
      activeIntervals: number;
      message: string;
    };
  };
  recommendations: string[];
}

interface SystemInfo {
  platform: string;
  arch?: string;
  architecture?: string;
  nodeVersion: string;
  uptime?: string | number;
  processUptime?: string;
  totalMemory: string | number;
  freeMemory: string | number;
  cpus?: number;
  cpuCount?: number;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export default function SystemHealth() {
  const [healthData, setHealthData] = useState<HealthCheckResult | null>(null);
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionResult, setActionResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchHealthData = async () => {
    try {
      setLoading(true);
      setError(null);

      const headers = getAuthHeaders();

      const [healthResponse, infoResponse] = await Promise.all([
        fetch('/api/admin/system-health', { headers }),
        fetch('/api/admin/system-health/info', { headers }),
      ]);

      if (!healthResponse.ok) throw new Error(`Health check failed: ${healthResponse.status}`);
      const healthResult = await healthResponse.json();
      if (healthResult.success) setHealthData(healthResult.data);

      if (infoResponse.ok) {
        const infoResult = await infoResponse.json();
        if (infoResult.success) setSystemInfo(infoResult.data);
      }

      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch system health data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();
    const interval = setInterval(fetchHealthData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Clear action result after 5 seconds
  useEffect(() => {
    if (actionResult) {
      const timer = setTimeout(() => setActionResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [actionResult]);

  const handleForceGC = async () => {
    setActionLoading('gc');
    try {
      const res = await fetch('/api/admin/system-health/gc', { method: 'POST', headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setActionResult({ type: 'success', message: `Garbage collection completed. Freed ${data.data?.freedMB?.toFixed(1) || 'some'} MB of memory.` });
        fetchHealthData();
      } else {
        setActionResult({ type: 'error', message: data.error || 'GC not available (requires --expose-gc flag)' });
      }
    } catch {
      setActionResult({ type: 'error', message: 'Failed to run garbage collection' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearCache = async () => {
    setActionLoading('cache');
    try {
      const res = await fetch('/api/cache', { method: 'DELETE', headers: getAuthHeaders(), body: JSON.stringify({ force: true }) });
      const data = await res.json();
      if (data.success) {
        setActionResult({ type: 'success', message: `Cache cleared. ${data.data?.clearedCount || 0} entries removed.` });
        fetchHealthData();
      } else {
        setActionResult({ type: 'error', message: data.error || 'Failed to clear cache' });
      }
    } catch {
      setActionResult({ type: 'error', message: 'Failed to clear cache' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearExpiredSessions = async () => {
    setActionLoading('sessions');
    try {
      const res = await fetch('/api/cache', {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ pattern: 'session:*', olderThan: 86400 })
      });
      const data = await res.json();
      if (data.success) {
        setActionResult({ type: 'success', message: `Expired sessions cleaned. ${data.data?.clearedCount || 0} entries removed.` });
      } else {
        setActionResult({ type: 'error', message: data.error || 'Failed to clear sessions' });
      }
    } catch {
      setActionResult({ type: 'error', message: 'Failed to clear expired sessions' });
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-emerald-700 bg-emerald-50 border-emerald-200';
      case 'warning': return 'text-amber-700 bg-amber-50 border-amber-200';
      case 'critical': return 'text-red-700 bg-red-50 border-red-200';
      default: return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'critical': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatUptime = (value: string | number | undefined) => {
    if (!value) return '—';
    // If it's a string like "2.50 hours", parse it
    if (typeof value === 'string') {
      const match = value.match(/([\d.]+)\s*hours?/i);
      if (match) {
        const totalHours = parseFloat(match[1]);
        const days = Math.floor(totalHours / 24);
        const hours = Math.floor(totalHours % 24);
        const minutes = Math.floor((totalHours * 60) % 60);
        if (days > 0) return `${days}d ${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
      }
      return value;
    }
    // If it's a number (seconds)
    if (isNaN(value)) return '—';
    const days = Math.floor(value / 86400);
    const hours = Math.floor((value % 86400) / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading && !healthData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="text-blue-600 animate-spin" size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md bg-white rounded-xl border border-red-200 p-8">
          <AlertTriangle className="text-red-500 mb-3" size={36} />
          <h2 className="text-lg font-bold text-slate-900 mb-1">Error Loading System Health</h2>
          <p className="text-slate-500 text-sm mb-4">{error}</p>
          <button onClick={fetchHealthData} className="px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 text-left">System Health</h1>
            <p className="text-slate-500 text-sm mt-0.5">Real-time diagnostics and maintenance actions</p>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-slate-400">Updated {lastUpdated.toLocaleTimeString()}</span>
            )}
            <button
              onClick={fetchHealthData}
              disabled={loading}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Action Result Toast */}
        {actionResult && (
          <div className={`mb-4 px-4 py-3 rounded-lg border text-sm font-medium flex items-center gap-2 ${
            actionResult.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            {actionResult.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            {actionResult.message}
          </div>
        )}

        {/* Overall Status + System Info */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {healthData && (
            <div className={`rounded-xl border p-4 ${getStatusColor(healthData.status)}`}>
              <p className="text-xs font-medium uppercase tracking-wider">Status</p>
              <p className="text-2xl font-bold mt-1 capitalize">{healthData.status}</p>
            </div>
          )}
          {systemInfo && (
            <>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Uptime</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{formatUptime(systemInfo.processUptime || systemInfo.uptime)}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">CPUs</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{systemInfo.cpuCount || systemInfo.cpus || '—'}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Infrastructure</p>
                <p className="text-sm font-semibold text-slate-900 mt-1">Managed by Yiontech</p>
                <p className="text-xs text-slate-400 mt-0.5">Hosting, security, backups & updates</p>
              </div>
            </>
          )}
        </div>

        {/* Infrastructure */}
        {systemInfo && (
          <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
            <h2 className="text-sm font-semibold text-slate-800 mb-3 text-left">Infrastructure Details</h2>
            <div className="flex items-center gap-6 text-sm">
              <div><span className="text-slate-500">Platform</span> <span className="font-medium text-slate-900 ml-2">{systemInfo.platform}</span></div>
              <div><span className="text-slate-500">Architecture</span> <span className="font-medium text-slate-900 ml-2">{systemInfo.architecture || systemInfo.arch || '—'}</span></div>
              <div><span className="text-slate-500">Node.js</span> <span className="font-medium text-slate-900 ml-2">{systemInfo.nodeVersion}</span></div>
              <div><span className="text-slate-500">Memory</span> <span className="font-medium text-slate-900 ml-2">{typeof systemInfo.totalMemory === 'string' ? systemInfo.totalMemory : `${(systemInfo.totalMemory / 1024 / 1024 / 1024).toFixed(1)} GB`}</span></div>
            </div>
          </div>
        )}

        {/* Maintenance Actions */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
          <h2 className="text-sm font-semibold text-slate-800 mb-3 text-left">Maintenance Actions</h2>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleForceGC}
              disabled={actionLoading !== null}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'gc' ? <RefreshCw size={16} className="animate-spin" /> : <Zap size={16} className="text-amber-500" />}
              Free Memory (GC)
            </button>
            <button
              onClick={handleClearCache}
              disabled={actionLoading !== null}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'cache' ? <RefreshCw size={16} className="animate-spin" /> : <Trash2 size={16} className="text-blue-500" />}
              Clear All Cache
            </button>
            <button
              onClick={handleClearExpiredSessions}
              disabled={actionLoading !== null}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-700 font-medium text-sm rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              {actionLoading === 'sessions' ? <RefreshCw size={16} className="animate-spin" /> : <Shield size={16} className="text-purple-500" />}
              Clean Expired Sessions
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-3 text-left">These actions help the system recover resources. The system also auto-optimises — GC runs automatically, cache entries expire by TTL, and stale sessions are cleaned periodically.</p>
        </div>

        {/* Health Checks */}
        {healthData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* Memory */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <HardDrive size={16} className="text-blue-600" /> Memory
                </h3>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(healthData.checks.memory.status)}`}>
                  {getStatusIcon(healthData.checks.memory.status)} {healthData.checks.memory.status}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Heap Used</span><span className="font-medium text-slate-900">{healthData.checks.memory.heapUsedMB.toFixed(1)} MB</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Heap Total</span><span className="font-medium text-slate-900">{healthData.checks.memory.heapTotalMB.toFixed(1)} MB</span></div>
                <div className="flex justify-between"><span className="text-slate-500">RSS</span><span className="font-medium text-slate-900">{healthData.checks.memory.rssMB.toFixed(1)} MB</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Usage</span><span className="font-medium text-slate-900">{healthData.checks.memory.percentUsed.toFixed(1)}%</span></div>
              </div>
              <div className="mt-3 w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                <div className={`h-2 rounded-full ${healthData.checks.memory.percentUsed > 85 ? 'bg-red-500' : healthData.checks.memory.percentUsed > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(healthData.checks.memory.percentUsed, 100)}%` }}></div>
              </div>
              <p className="text-xs text-slate-400 mt-2">{healthData.checks.memory.message}</p>
            </div>

            {/* CPU */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Cpu size={16} className="text-emerald-600" /> CPU
                </h3>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(healthData.checks.cpu.status)}`}>
                  {getStatusIcon(healthData.checks.cpu.status)} {healthData.checks.cpu.status}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">CPU Usage</span><span className="font-medium text-slate-900">{healthData.checks.cpu.usagePercent.toFixed(1)}%</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Load Average</span><span className="font-medium text-slate-900">{healthData.checks.cpu.loadAverage.map(l => l.toFixed(2)).join(', ')}</span></div>
              </div>
              <div className="mt-3 w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                <div className={`h-2 rounded-full ${healthData.checks.cpu.usagePercent > 90 ? 'bg-red-500' : healthData.checks.cpu.usagePercent > 60 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(healthData.checks.cpu.usagePercent, 100)}%` }}></div>
              </div>
              <p className="text-xs text-slate-400 mt-2">{healthData.checks.cpu.message}</p>
            </div>

            {/* Database */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Database size={16} className="text-purple-600" /> Database
                </h3>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(healthData.checks.database.status)}`}>
                  {getStatusIcon(healthData.checks.database.status)} {healthData.checks.database.status}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Connected</span><span className="font-medium text-slate-900">{healthData.checks.database.connected ? 'Yes' : 'No'}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Response Time</span><span className="font-medium text-slate-900">{healthData.checks.database.responseTime}ms</span></div>
              </div>
              <p className="text-xs text-slate-400 mt-3">{healthData.checks.database.message}</p>
            </div>

            {/* Processes */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                  <Activity size={16} className="text-orange-600" /> Processes
                </h3>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${getStatusColor(healthData.checks.processes.status)}`}>
                  {getStatusIcon(healthData.checks.processes.status)} {healthData.checks.processes.status}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Total Processes</span><span className="font-medium text-slate-900">{healthData.checks.processes.totalProcesses}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Zombie Processes</span><span className="font-medium text-slate-900">{healthData.checks.processes.zombieCount}</span></div>
              </div>
              <p className="text-xs text-slate-400 mt-3">{healthData.checks.processes.message}</p>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {healthData && healthData.recommendations.length > 0 && (
          <div className="bg-white rounded-xl border border-amber-200 p-5">
            <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
              <AlertTriangle size={16} className="text-amber-500" /> Recommendations
            </h2>
            <ul className="space-y-2">
              {healthData.recommendations.map((rec, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-amber-500 mt-0.5">&#8226;</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}

      </div>
    </div>
  );
}
